import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MoneyInput } from '../MoneyInput';
import { Label } from '../ui/label';
import { HandCoins, Plus, Trash2 } from 'lucide-react';
import PurchaseInvoiceForm from '../PurchaseInvoiceForm';

const fmtCurrency = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

export function SettleAdvanceDialog({
  open, onOpenChange, onCancel, advance, form, setForm,
  setSettleExpense, rmSettleExpense, addSettleExpense,
  addComprobante, setComprobante, rmComprobante,
  expenseConcepts, suppliers, withholdingTypes,
  settleRendido, settleDiff, cashBanks, onConfirm, settling,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="w-5 h-5 text-amber-600" />
            Rendir y cerrar anticipo {advance?.payment_order_number}
          </DialogTitle>
          <DialogDescription>
            Entregado: <strong>{advance && fmtCurrency(advance.total_amount)}</strong> a {advance?.recipient_name || '—'}. Cargá los gastos reales con su comprobante.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha de rendición *</Label>
              <Input type="date" max={new Date().toISOString().slice(0, 10)} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="settle-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Gastos rendidos <span className="text-muted-foreground font-normal text-xs">(sin comprobante fiscal)</span></Label>
            {form.expenses.map((ex, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={ex.expense_concept_id} onChange={(e) => setSettleExpense(i, { expense_concept_id: e.target.value })}
                  className="flex h-10 flex-1 rounded-md border border-input bg-background px-2 text-sm" data-testid={`settle-concept-${i}`}>
                  <option value="">Concepto…</option>
                  {expenseConcepts.map(ec => <option key={ec.concept_id} value={ec.concept_id}>{ec.name}</option>)}
                </select>
                <Input className="w-40" placeholder="Comprobante" value={ex.reference} onChange={(e) => setSettleExpense(i, { reference: e.target.value })} data-testid={`settle-ref-${i}`} />
                <MoneyInput className="w-32" placeholder="$ 0,00" value={ex.amount} onChange={(e) => setSettleExpense(i, { amount: e.target.value })} data-testid={`settle-amount-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => rmSettleExpense(i)} disabled={form.expenses.length <= 1}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSettleExpense} data-testid="settle-add-expense"><Plus className="w-4 h-4 mr-1" /> Agregar gasto</Button>
          </div>

          <div className="space-y-2 border-t pt-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Comprobantes fiscales <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                <p className="text-[11px] text-muted-foreground">Para que la compra figure en el Libro IVA Compras. No afecta la cuenta corriente del proveedor.</p>
              </div>
              <Button variant="outline" size="sm" onClick={addComprobante} data-testid="settle-add-comprobante"><Plus className="w-4 h-4 mr-1" /> Comprobante</Button>
            </div>
            {form.comprobantes.map((c, i) => (
              <PurchaseInvoiceForm key={i} index={i} value={c} suppliers={suppliers} expenseConcepts={expenseConcepts} withholdingTypes={withholdingTypes}
                onChange={(val) => setComprobante(i, val)} onRemove={() => rmComprobante(i)} />
            ))}
          </div>

          <div className="rounded-md border p-3 bg-muted/40 space-y-1 text-sm" data-testid="settle-summary">
            <div className="flex justify-between"><span>Anticipo entregado</span><strong>{advance && fmtCurrency(advance.total_amount)}</strong></div>
            <div className="flex justify-between"><span>Total rendido</span><strong>{fmtCurrency(settleRendido)}</strong></div>
            <div className={`flex justify-between font-semibold ${settleDiff > 0 ? 'text-emerald-700' : settleDiff < 0 ? 'text-rose-700' : ''}`}>
              <span>{settleDiff > 0 ? 'Devolución (sobró)' : settleDiff < 0 ? 'Reposición (faltó)' : 'Diferencia'}</span>
              <span>{fmtCurrency(Math.abs(settleDiff))}</span>
            </div>
          </div>

          {settleDiff !== 0 && (
            <div>
              <Label>{settleDiff > 0 ? 'Caja donde ENTRA la devolución *' : 'Caja de donde SALE la reposición *'}</Label>
              <select value={form.difference_cash_bank_id} onChange={(e) => setForm({ ...form, difference_cash_bank_id: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="settle-diff-cashbank">
                <option value="">Seleccionar caja/banco…</option>
                {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onConfirm} disabled={settling} className="bg-amber-600 hover:bg-amber-700" data-testid="settle-confirm-btn">
            {settling ? 'Rindiendo…' : 'Rendir y cerrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
